import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Paper, Stack, Typography, Chip, Button, Divider, Alert, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { api } from '../api/client';
import type { AccessEventDetailDto } from '../api/types';

function formatDuration(seconds?: number): string {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['access-event', id],
    queryFn: () => api.get<AccessEventDetailDto>(`/access-events/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error || !data) return <Alert severity="error">No se pudo cargar el evento.</Alert>;

  const ev = data.event;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => nav(-1)}>Volver</Button>
        <Typography variant="h4">Evento #{ev.id}</Typography>
        <Chip label={ev.status} color={
          ev.status === 'PROCESSED' ? 'success'
          : ev.status === 'OVERRIDDEN' ? 'primary'
          : ev.status === 'MANUAL_REVIEW' ? 'warning'
          : ev.status === 'FAILED' ? 'error' : 'default'
        } />
        {ev.eventType && <Chip label={ev.eventType === 'ENTRY' ? 'Entrada' : ev.eventType === 'EXIT' ? 'Salida' : 'No resuelto'}
          color={ev.eventType === 'ENTRY' ? 'success' : ev.eventType === 'EXIT' ? 'info' : 'warning'} />}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 320 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Imagen</Typography>
          {ev.imagePresignedUrl ? (
            <img src={ev.imagePresignedUrl} alt="placa" style={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 4 }} />
          ) : (
            <Alert severity="warning">Imagen no disponible en S3</Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
            S3 key: {ev.s3Key}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, flex: 1.2, minWidth: 320 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Detalle</Typography>
          <Stack spacing={1.5}>
            <Field label="Capturado" value={new Date(ev.capturedAt).toLocaleString()} />
            <Field label="Placa leída" value={ev.detectedPlate ?? '(no legible)'} mono={!!ev.detectedPlate} />
            {data.detectedPlateRaw && data.detectedPlateRaw !== ev.detectedPlate && (
              <Field label="Placa raw IA" value={data.detectedPlateRaw} mono />
            )}
            <Field label="Confianza" value={ev.confidence != null ? Number(ev.confidence).toFixed(3) : '—'} />
            <Field label="Modelo IA" value={data.aiModel ?? '—'} />
            {ev.errorCode && <Field label="Código error" value={ev.errorCode} />}
            {ev.errorMessage && <Field label="Mensaje error" value={ev.errorMessage} multiline />}

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Camioneta asignada</Typography>
            {ev.vehicle ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={ev.vehicle.plateDisplay} sx={{ fontFamily: 'monospace' }} />
                <Typography variant="body2">{ev.vehicle.driverName ?? '(sin chofer)'}</Typography>
                <Button size="small" onClick={() => nav(`/vehicles/${ev.vehicle!.id}`)}>Ver camioneta</Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No asignada</Typography>
            )}
          </Stack>
        </Paper>
      </Stack>

      {data.visit && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Visita asociada #{data.visit.id}</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Field label="Entrada" value={new Date(data.visit.entryAt).toLocaleString()} />
            <Field label="Salida" value={data.visit.exitAt ? new Date(data.visit.exitAt).toLocaleString() : 'En curso'} />
            <Field label="Duración" value={formatDuration(data.visit.durationSeconds)} />
            <Field label="Chofer al momento" value={data.visit.driverNameSnapshot ?? '—'} />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {data.visit.entryEventId !== ev.id && (
              <Button size="small" onClick={() => nav(`/history/${data.visit!.entryEventId}`)}>
                Ver evento de entrada (#{data.visit.entryEventId})
              </Button>
            )}
            {data.visit.exitEventId && data.visit.exitEventId !== ev.id && (
              <Button size="small" onClick={() => nav(`/history/${data.visit!.exitEventId}`)}>
                Ver evento de salida (#{data.visit.exitEventId})
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {data.override && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Override manual</Typography>
          <Stack spacing={1}>
            <Field label="Aplicado" value={new Date(data.override.createdAt).toLocaleString()} />
            <Field label="Operador (user id)" value={String(data.override.userId)} />
            <Field label="Vehículo previo" value={data.override.previousVehicleId != null ? String(data.override.previousVehicleId) : '—'} />
            <Field label="Vehículo nuevo" value={data.override.newVehicleId != null ? String(data.override.newVehicleId) : '—'} />
            <Field label="Tipo previo" value={data.override.previousEventType ?? '—'} />
            <Field label="Tipo nuevo" value={data.override.newEventType ?? '—'} />
            <Field label="Razón" value={data.override.reason} multiline />
          </Stack>
        </Paper>
      )}

      {data.aiRawResponse && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Respuesta cruda de la IA</Typography>
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto' }}>
            {data.aiRawResponse}
          </pre>
        </Paper>
      )}
    </Box>
  );
}

function Field({ label, value, mono, multiline }: { label: string; value: string; mono?: boolean; multiline?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: mono ? 'monospace' : undefined, whiteSpace: multiline ? 'pre-wrap' : undefined }}
      >
        {value}
      </Typography>
    </Box>
  );
}
